import { ts } from '@shared/lib/diagTs';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '@core/api/useApiClient';
import { useAccessTokenProvider } from '@core/auth/useAccessToken';
import { getCqgRelay, setCqgTokenGetter } from '../lib/cqgRelay';
import { readStoredCqgCreds } from '../lib/cqgCreds';
import { useCqgConnect, useRequestConcurrentLogin } from '../hooks/useCqgConnect';
import { type CqgStatus } from '../hooks/useCqgDisplayState';
import { useAnyConnectSurfaceOpen } from '@features/accounts/state/connectDialogStore';
import { useAccounts } from '@features/trades/hooks/useTradeMetadata';
import { accountOwnedByLogin } from '@features/accounts/lib/accountConnection';
import { readAccountLogin } from '@features/accounts/lib/accountLogins';
import { BRAND_GRADIENT } from '@shared/styles/brand';

/**
 * Reconnect / take-over confirmation popup (Account Sync Workflow, refresh-resume path).
 *
 * On a page refresh the FE auto-reconnects a prior CQG session (see the dialog's page-load resume). If
 * that reconnect can't proceed because the user's OTHER CQG session (their trading platform) now holds
 * the single allowed slot, CQG reports `reason=concurrent_session` and the resume STOPS there (we never
 * silently kick the platform — that's the logon war). This app-wide popup then asks the user to CONFIRM
 * the reconnect; confirming re-logs-on with `drop_concurrent_session=true` (take over). "Not now" leaves
 * the platform running and submits the concurrent-login request instead.
 *
 * Mounted once in AppShell (like CqgConnectedPopup) so it surfaces without opening the connector dialog.
 * Only shows when we actually have stored creds to reconnect with.
 */
export function CqgReconnectPopup() {
  const api = useApiClient();
  const getToken = useAccessTokenProvider();
  const tokenRef = useRef(getToken);
  tokenRef.current = getToken;
  const connect = useCqgConnect();
  const requestConcurrent = useRequestConcurrentLogin();
  const [dismissed, setDismissed] = useState(false);

  // Shared ['cqg','status'] cache (poll + the dialog's cqg.state push both write it), so this popup and
  // the badge/dialog agree on the concurrent-session state.
  const status = useQuery({
    queryKey: ['cqg', 'status'],
    queryFn: () => api.get<CqgStatus>('/api/v1/cqg/status'),
    refetchInterval: 60_000,
  });
  const reason = status.data?.reason ?? null;
  const isConcurrent = reason === 'concurrent_session';
  const creds = readStoredCqgCreds();
  const hasCreds = !!creds.username;

  // Which journal accounts this reconnect actually signs in (2026-07-31 user request: "in case I
  // have more than 1 CQG account connected, elaborate ... so I'll know to which CQG should I
  // connect"). The take-over targets ONE CQG login — the stored username+environment — so the popup
  // names that login and splits the user's CQG accounts into signed-in vs untouched. Shares the
  // ['trades','accounts'] cache; the Array.isArray guard covers an errored/foreign response shape.
  const accountsQuery = useAccounts();
  const cqgAccounts = (Array.isArray(accountsQuery.data) ? accountsQuery.data : [])
    .filter(a => a.provider === 'cqg');
  const owned = (a: (typeof cqgAccounts)[number]) => accountOwnedByLogin(
    a,
    { environment: creds.environment, username: creds.username || null },
    readAccountLogin(a.name)?.username ?? null,
  );
  const covered = cqgAccounts.filter(a => owned(a));
  const untouched = cqgAccounts.filter(a => !owned(a));
  const label = (a: (typeof cqgAccounts)[number]) => a.displayName || a.name;
  // Suppress while a connect surface (connector dialog / Add-account dialog) is open — those carry
  // their OWN take-over UI, and showing both stacks two competing take-over buttons that can each
  // fire a connect (2026-07-18 review). The dialog closing re-renders this and the popup resurfaces
  // if the concurrent state still holds.
  const connectSurfaceOpen = useAnyConnectSurfaceOpen();

  // Re-arm when the concurrent state clears, so a later reconnect can prompt again.
  useEffect(() => { if (!isConcurrent) setDismissed(false); }, [isConcurrent]);

  if (!isConcurrent || !hasCreds || dismissed || connectSurfaceOpen) return null;

  const confirmReconnect = () => {
    // Ensure the browser-relay hub is up (token-getter via ref, same as the dialog), then arm the
    // one-shot take-over flag + credential-less connect: the BE pushes CqgOpenSocket and the relay
    // re-logons itself from stored creds with drop_concurrent_session=true so CQG hands us the slot.
    // (The POST carries NO credentials — they never transit our BE.)
    setCqgTokenGetter(() => tokenRef.current());
    void getCqgRelay().start()
      .catch((e) => console.error(ts(), '[cqg-reconnect-popup] relay start failed', e))
      .finally(() => connect.mutate({ takeOver: true }));
    setDismissed(true);
  };

  const notNow = () => {
    // Keep the platform running — set up a concurrent login instead of taking over. Only a LIVE
    // session needs an FCM concurrent login (demo creds don't clash with the real trading platform).
    if (creds.environment === 'live') requestConcurrent.mutate();
    setDismissed(true);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Reconnect your CQG session">
      <div className="w-full max-w-md rounded-2xl bg-canvas p-6 text-text shadow-2xl">
        <h2 className="text-[17px] font-bold">Reconnect your CQG session?</h2>
        <p className="mt-3 text-[14px] leading-6 text-text-muted">
          Your CQG account is currently in use by your trading platform. Reconnecting the journal here will
          <span className="font-semibold text-text"> sign you out of that other session</span> so the journal
          can take over. You can keep both running once your concurrent login is set up.
        </p>

        {/* WHICH login this take-over targets (2026-07-31): with several CQG accounts connected, the
            popup names the stored login and splits the accounts into signed-in vs untouched, so the
            user knows exactly what "Reconnect" affects before kicking their platform session. */}
        <div className="mt-4 rounded-xl px-4 py-3" style={{ background: 'var(--canvas-inset)' }}>
          <p className="text-[10px] font-semibold uppercase" style={{ letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
            Reconnecting CQG login
          </p>
          <p className="mt-1 flex items-center gap-2 text-[14px] font-semibold text-text">
            {creds.username}
            <span
              className="rounded-full px-2 text-[10px] font-bold uppercase"
              style={{
                lineHeight: '18px', letterSpacing: '0.05em',
                background: creds.environment === 'demo' ? 'var(--pill-short-bg)' : 'var(--pill-win-bg)',
                color: creds.environment === 'demo' ? 'var(--chip-text)' : 'var(--gain)',
              }}
            >
              {creds.environment === 'demo' ? 'Demo' : 'Live'}
            </span>
          </p>
          {covered.length > 0 && (
            <p className="mt-1.5 text-[12px] leading-5 text-text-muted">
              Signs in {covered.length === 1 ? 'account' : `${covered.length} accounts`}:{' '}
              <span className="font-semibold text-text">{covered.map(label).join(' · ')}</span>
            </p>
          )}
          {untouched.length > 0 && (
            <p className="mt-1 text-[12px] leading-5 text-text-muted">
              Not affected ({untouched.length === 1 ? 'a different CQG login' : 'different CQG logins'}):{' '}
              {untouched.map(label).join(' · ')}
            </p>
          )}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button
            type="button"
            onClick={notNow}
            className="rounded-lg px-4 py-2 text-[13px] font-semibold text-text-muted transition-opacity hover:opacity-80"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={confirmReconnect}
            disabled={connect.isPending}
            className="rounded-lg px-4 py-2 text-[13px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ background: BRAND_GRADIENT }}
          >
            {connect.isPending ? 'Reconnecting…' : 'Reconnect'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
